import { Directive, OnInit, Optional } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ItemView, ItemViewProviderDirective } from '@ntap/core';
import { first } from 'rxjs';

import { OibService } from '@app/shared/services/oib.service';

/**
 * Bazna klasa za OIB servis komponente.
 * Sadrži zajedničku logiku za prikaz dijaloga, pretragu OIB-a i primjenu rezultata.
 *
 * Podklase trebaju implementirati:
 * - `checkExistingOib()` — logika za provjeru postojećeg OIB-a na entitetu
 * - `applyOibData()` — logika za primjenu OIB podataka na entitet
 * - `getNaturalOnly()` — vraća true ako se pretražuju samo fizičke osobe
 */
@Directive()
export abstract class OibServiceBaseComponent implements OnInit {
  showDialog = false;
  oibResponse: any = null;
  oib = new FormControl<string | null>(null, [
    Validators.required,
    Validators.minLength(11),
    Validators.maxLength(11),
    Validators.pattern('^[0-9]*$')
  ]);
  parrentFormData: any;
  dirty = false;
  applyError: any = null;
  fetchError: any = null;
  oibReadonly = false;
  loadingOib = false;

  constructor(
    protected oibService: OibService,
    @Optional()
    protected itemViewProviderDirective: ItemViewProviderDirective<ItemView>
  ) {}

  ngOnInit(): void {
    if (!this.itemViewProviderDirective) {
      return;
    }

    this.itemViewProviderDirective.item$.subscribe((item) => {
      this.parrentFormData = item;
    });

    this.onInit();
  }

  protected onInit(): void {}

  doShowDialog(showDialog: boolean) {
    this.showDialog = showDialog;
    this.applyError = null;
    this.fetchError = null;
    this.dirty = false;
    this.oibResponse = null;
    this.loadingOib = false;
    if (showDialog) {
      this.checkExistingOib();
    }
  }

  /**
   * Provjera postojećeg OIB-a na entitetu.
   * Svaka podklasa implementira svoju logiku.
   */
  protected abstract checkExistingOib(): void;

  /**
   * Primjena OIB podataka na entitet.
   * Svaka podklasa implementira svoju logiku.
   */
  protected abstract applyOibData(): void;

  /**
   * Vraća true ako se pretražuju samo fizičke osobe.
   * Podrazumijevano false — podklase mogu override-ati.
   */
  protected getNaturalOnly(): boolean {
    return false;
  }

  apply() {
    if (!this.itemViewProviderDirective) {
      return;
    }

    this.applyError = null;
    return this.applyOibData();
  }

  search() {
    this.fetchError = null;
    this.applyError = null;
    this.loadingOib = true;

    const naturalOnly = this.getNaturalOnly();

    return this.oibService
      .searchPerson(this.oib.value!, naturalOnly)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.dirty = true;
          this.oibResponse = response;
          this.loadingOib = false;
        },
        error: (e) => {
          this.fetchError =
            e.error?.message || e.message || 'Greška kod dohvata podataka!';
          this.loadingOib = false;
        }
      });
  }

  /** Helper za standardnu obradu greške kod primjene */
  protected handleApplyError(e: any): void {
    this.applyError =
      e.error?.message || e.message || 'Greška kod spremanja podataka!';
  }
}
